import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, List, ListItem, ListItemText, Divider } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useUser } from '../../contexts/UserContext';
import { formatDobForDisplay } from '../../utils';

interface SelectChildModalProps {
  open: boolean;
  onClose: () => void;
}

const SelectChildModal: React.FC<SelectChildModalProps> = ({ open, onClose }) => {
  const { user, setSelectedChild } = useUser();

  const handleSelectChild = (child: any) => {
    setSelectedChild(child);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 2,
          pt: 3,
          fontSize: '1.5rem',
          fontWeight: 700,
        }}
      >
        Select a Child
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {user?.children && user.children.length > 0 ? (
          <List sx={{ py: 0 }}>
            {user.children.map((child, index) => (
              <React.Fragment key={child.id}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 2,
                    mb: 1,
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      backgroundColor: '#EEEEEE',
                      borderColor: '#297AB0',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#297AB0',
                      color: 'white',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <PersonIcon />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#1a1a1a',
                          mb: 0.5,
                        }}
                      >
                        {child.fname} {child.lname}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontSize: '0.875rem',
                        }}
                      >
                        Date of Birth: {formatDobForDisplay(child.dob)}
                      </Typography>
                    }
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleSelectChild(child)}
                    sx={{
                      backgroundColor: '#297AB0',
                      color: 'white',
                      px: 3,
                      py: 1,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#1f5d85',
                      },
                    }}
                  >
                    Select
                  </Button>
                </ListItem>
                {index < user.children.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              No children found. Please add a child to your profile.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#666',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectChildModal;